import { useAtomValue } from "jotai";
import { useQuery } from "@tanstack/react-query";
import { queriesEnabledAtom, tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { useMemo } from "react";
import { getUserWorkspacePermissions } from "../../../utils/UserUtils";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import useUser from "../auth/useUser";
import TenantAPI from "../../api/TenantAPI";

const useTenantUsers = (disabled = false) => {
  const queriesEnabled = useAtomValue(queriesEnabledAtom);
  const tenantID = useAtomValue(tenantIDAtom);
  const workspaceID = useAtomValue(workspaceIdAtom);
  const { wPermissions: permissions } = useUser();

  const tenantUsers = useQuery({
    queryKey: ["tenantUsers", tenantID],
    queryFn: () =>
      TenantAPI.getUsersFromTenant().then((data) => {
        return data.data.data;
      }),
    enabled: queriesEnabled && !disabled && !!tenantID,
  });

  const tenantUsersByRole = useMemo(() => {
    if (!tenantUsers.data || tenantUsers.data.length < 1) return [];

    let userPermissionIds: { [key: string]: any[] } = {
      Administrators: [],
      Architects: [],
      Contributors: [],
      Viewers: [],
    };

    for (const user of tenantUsers.data) {
      const userPermissionID: any = getUserWorkspacePermissions(
        user,
        tenantID,
        workspaceID,
        permissions
      );

      if (!userPermissionID) return userPermissionIds;

      if (
        userPermissionID.Name === "Workspace Owner" ||
        userPermissionID.Name === "Workspace Administrator"
      ) {
        userPermissionIds.Administrators.push(user.id);
        continue;
      }

      if (userPermissionID.Name === "Workspace Architect") {
        userPermissionIds.Architects.push(user.id);
        continue;
      }

      if (userPermissionID.Name === "Workspace Contributor") {
        userPermissionIds.Contributors.push(user.id);
        continue;
      } else {
        userPermissionIds.Viewers.push(user.id);
      }
    }

    return userPermissionIds;
  }, [tenantUsers.data, tenantID, workspaceID, permissions]);

  return {
    users: tenantUsers.data,
    usersLoading: tenantUsers.isLoading || queriesEnabled === false,
    tenantUsersByRole: tenantUsersByRole,
  };
};

export default useTenantUsers;
